import React, { useRef, useState, useEffect } from 'react';
import Gallery from './Gallery';
import { galleryData } from '../data/galleryData';
import { discoverGalleryPhotos, mergeGalleryPhotos } from '../utils/photoDiscovery';
import { 
  fetchWeatherData, 
  fetchAirQuality, 
  fetchSunriseSunset,
  fetchLocalEvents,
  fetchLocalNews,
  getSouthingtonFacts,
  getWeatherDescription,
  getAQIDescription,
  formatLocalTime
} from '../utils/southingtonAPI';
import './EnhancedComponents.css';

const SouthingtonEnhanced = () => {
  const galleryRef = useRef();
  const [showMore, setShowMore] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [photos, setPhotos] = useState(galleryData.apple || []);
  
  // State for dynamic content
  const [weatherData, setWeatherData] = useState(null);
  const [airQuality, setAirQuality] = useState(null);
  const [sunData, setSunData] = useState(null);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllDynamicContent();
  }, []);

  // Discover additional photos from S3
  useEffect(() => {
    const loadDiscoveredPhotos = async () => {
      try {
        const discoveredPhotos = await discoverGalleryPhotos('apple');
        const mergedPhotos = mergeGalleryPhotos(galleryData.apple || [], discoveredPhotos);
        setPhotos(mergedPhotos);
      } catch (error) {
        console.warn('Could not load discovered photos:', error);
        setPhotos(galleryData.apple || []);
      }
    };

    loadDiscoveredPhotos();
  }, []);

  const fetchAllDynamicContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [weatherResult, airResult, sunResult, eventsResult, newsResult] = await Promise.allSettled([
        fetchWeatherData(),
        fetchAirQuality(),
        fetchSunriseSunset(),
        fetchLocalEvents(),
        fetchLocalNews()
      ]);

      if (weatherResult.status === 'fulfilled' && weatherResult.value) {
        setWeatherData(weatherResult.value);
      }
      
      if (airResult.status === 'fulfilled' && airResult.value) {
        setAirQuality(airResult.value);
      }
      
      if (sunResult.status === 'fulfilled' && sunResult.value) {
        setSunData(sunResult.value);
      }
      
      if (eventsResult.status === 'fulfilled') {
        setEvents(eventsResult.value);
      }
      
      if (newsResult.status === 'fulfilled') {
        setNews(newsResult.value);
      }

    } catch (err) {
      console.error('Error fetching dynamic content:', err);
      setError('Unable to load some dynamic content');
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (weathercode) => {
    const weatherIcons = {
      0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️', 45: '🌫️', 48: '🌫️',
      51: '🌦️', 53: '🌦️', 55: '🌦️', 61: '🌧️', 63: '🌧️', 65: '🌧️',
      71: '🌨️', 73: '🌨️', 75: '🌨️', 95: '⛈️', 96: '⛈️', 99: '⛈️'
    };
    return weatherIcons[weathercode] || '🌤️';
  };

  const formatTemperature = (temp) => {
    return temp ? `${Math.round(temp)}°C (${Math.round(temp * 9/5 + 32)}°F)` : 'N/A';
  };

  const handleCelebration = () => {
    setShowMore(!showMore);
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'weather':
        return (
          <div className="tab-content weather-tab">
            {weatherData && (
              <div className="weather-details">
                <div className="current-weather">
                  <h4>Current Conditions</h4>
                  <div className="weather-main">
                    <span className="weather-icon-large">
                      {getWeatherIcon(weatherData.current_weather?.weathercode)}
                    </span>
                    <div className="weather-text">
                      <div className="temperature-large">
                        {formatTemperature(weatherData.current_weather?.temperature)}
                      </div>
                      <div className="weather-desc">
                        {getWeatherDescription(weatherData.current_weather?.weathercode)}
                      </div>
                      {weatherData.current_weather?.windspeed && (
                        <div className="wind-info">
                          <i className="fas fa-wind"></i>
                          Wind: {Math.round(weatherData.current_weather.windspeed)} km/h
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {weatherData.daily && (
                  <div className="forecast">
                    <h4>3-Day Forecast</h4>
                    <div className="forecast-grid">
                      {weatherData.daily.time.slice(0, 3).map((date, index) => (
                        <div key={date} className="forecast-day">
                          <div className="day-name">
                            {index === 0 ? 'Today' : new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </div>
                          <div className="forecast-icon">
                            {getWeatherIcon(weatherData.daily.weathercode[index])}
                          </div>
                          <div className="temps">
                            <span className="high">{Math.round(weatherData.daily.temperature_2m_max[index])}°</span>
                            <span className="low">{Math.round(weatherData.daily.temperature_2m_min[index])}°</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {airQuality && airQuality.current && (
                  <div className="air-quality">
                    <h4>Air Quality</h4>
                    <div className="aqi-info">
                      <div className="aqi-value" style={{color: getAQIDescription(airQuality.current.us_aqi).color}}>
                        AQI: {airQuality.current.us_aqi}
                      </div>
                      <div className="aqi-level">
                        {getAQIDescription(airQuality.current.us_aqi).level}
                      </div>
                      <div className="aqi-desc">
                        {getAQIDescription(airQuality.current.us_aqi).description}
                      </div>
                    </div>
                  </div>
                )}

                {sunData && (
                  <div className="sun-times">
                    <h4>Sun Times</h4>
                    <div className="sun-info">
                      <div className="sun-item">
                        <i className="fas fa-sun"></i>
                        <span>Sunrise: {formatLocalTime(sunData.sunrise)}</span>
                      </div>
                      <div className="sun-item">
                        <i className="fas fa-moon"></i>
                        <span>Sunset: {formatLocalTime(sunData.sunset)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'events':
        return (
          <div className="tab-content events-tab">
            <h4>Upcoming Events</h4>
            {events.length > 0 ? (
              <div className="events-list">
                {events.map(event => (
                  <div key={event.id} className="event-item">
                    <div className="event-header">
                      <h5>{event.title}</h5>
                      <span className="event-category">{event.category}</span>
                    </div>
                    <div className="event-details">
                      <div className="event-time">
                        <i className="fas fa-calendar"></i>
                        {new Date(event.date).toLocaleDateString()} at {event.time}
                      </div>
                      <div className="event-location">
                        <i className="fas fa-map-marker-alt"></i>
                        {event.location}
                      </div>
                    </div>
                    <p className="event-description">{event.description}</p>
                    <a href={event.url} target="_blank" rel="noreferrer" className="event-link">
                      Learn More <i className="fas fa-external-link-alt"></i>
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <p>No upcoming events available.</p>
            )}
          </div>
        );

      case 'news':
        return (
          <div className="tab-content news-tab">
            <h4>Local News</h4>
            {news.length > 0 ? (
              <div className="news-list">
                {news.map(article => (
                  <div key={article.id} className="news-item">
                    <div className="news-header">
                      <h5>
                        <a href={article.url} target="_blank" rel="noreferrer">
                          {article.title}
                        </a>
                      </h5>
                      <span className="news-category">{article.category}</span>
                    </div>
                    <p className="news-summary">{article.summary}</p>
                    <div className="news-meta">
                      <span className="news-source">{article.source}</span>
                      <span className="news-date">
                        {new Date(article.publishedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No recent news available.</p>
            )}
          </div>
        );

      case 'facts':
        return (
          <div className="tab-content facts-tab">
            <h4>Did You Know?</h4>
            <div className="facts-list">
              {getSouthingtonFacts().map((fact, index) => (
                <div key={index} className="fact-item">
                  <div className="fact-category">{fact.category}</div>
                  <div className="fact-text">{fact.fact}</div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="tab-content overview-tab">
            {/* Quick Weather Summary */}
            {weatherData && (
              <div className="weather-summary">
                <div className="weather-quick">
                  <span className="weather-icon">
                    {getWeatherIcon(weatherData.current_weather?.weathercode)}
                  </span>
                  <span className="temp-quick">
                    {formatTemperature(weatherData.current_weather?.temperature)}
                  </span>
                  <span className="condition-quick">
                    {getWeatherDescription(weatherData.current_weather?.weathercode)}
                  </span>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="stat-item">
                <strong>Population:</strong> ~43,000
              </div>
              <div className="stat-item">
                <strong>Founded:</strong> 1779
              </div>
              <div className="stat-item">
                <strong>Area:</strong> 36.4 sq mi
              </div>
              <div className="stat-item">
                <strong>Elevation:</strong> 200-900 ft
              </div>
            </div>

            {/* Quick Links */}
            <div className="quick-links">
              <a href="https://www.southington.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-building"></i>
                Town Hall
              </a>
              <a href="https://www.southingtonlibrary.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-book"></i>
                Library
              </a>
              <a href="https://www.southingtonschools.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-school"></i>
                Schools
              </a>
              <a href="https://www.southingtonchamber.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-handshake"></i>
                Chamber
              </a>
              <a href="https://southingtonwater.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-tint"></i>
                Water Dept
              </a>
              <a href="https://www.unitedwayofsouthington.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-hands-helping"></i>
                United Way
              </a>
              <a href="https://ctvisit.com/listings/town-southington" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-map-signs"></i>
                CT Visit
              </a>
              <a href="https://mountsouthington.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-mountain"></i>
                Mt. Southington
              </a>
              <a href="https://www.southingtonlandtrust.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-tree"></i>
                Land Trust
              </a>
              <a href="https://www.calendarhouse.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-home"></i>
                Calendar House
              </a>
              <a href="https://southingtonrotary.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-users"></i>
                Rotary Club
              </a>
              <a href="https://www.southingtonoutsider.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-newspaper"></i>
                Outsider News
              </a>
              <a href="https://www.southingtonearlychildhood.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-baby"></i>
                Early Childhood
              </a>
              <a href="https://www.southingtontv.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-tv"></i>
                Southington TV
              </a>
              <a href="https://www.sccymca.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-dumbbell"></i>
                YMCA
              </a>
              <a href="https://www.southingtonsoccer.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-futbol"></i>
                Soccer Club
              </a>
              <a href="https://rogersorchards.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-apple-alt"></i>
                Rogers Orchards
              </a>
              <a href="https://www.karabinfarms.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-seedling"></i>
                Karabin Farms
              </a>
              <a href="https://southingtoncountryclub.com/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-golf-ball"></i>
                Country Club
              </a>
              <a href="https://www.thebarnesmuseum.org/" target="_blank" rel="noreferrer" className="quick-link">
                <i className="fas fa-university"></i>
                Barnes Museum
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <section id="apple" className="section apple-section enhanced-southington">
      <div className="section-header">
        <img src="/images/apple.png" alt="Apple" className="section-icon" />
        <h1>Southington, CT. 06489</h1>
      </div>

      <p>
        <b><a href="https://www.southington.org/" target="_blank" rel="noreferrer">Southington CT</a></b> is situated about 20 miles southwest of Hartford, 80 miles northeast of New York City, 105 miles southwest of Boston and 77 miles west of Providence.
        The town includes the distinct areas of <strong>Plantsville</strong>, <strong>Marion</strong>, and <strong>Milldale</strong>, each with its own post office, zip code, and unique architectural character, along with the main Southington area (06489).
      </p>

      {/* Southington Gallery - Moved to top */}
      <div className="futbol-gallery-section">
        <Gallery 
          ref={galleryRef} 
          images={photos} 
          sectionName="Southington Gallery"
        />
      </div>

      {/* Dynamic Content Tabs - Always Visible */}
      <div className="content-tabs">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-home"></i>
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'weather' ? 'active' : ''}`}
            onClick={() => setActiveTab('weather')}
          >
            <i className="fas fa-cloud-sun"></i>
            Weather
          </button>
          <button 
            className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            <i className="fas fa-calendar-alt"></i>
            Events
          </button>
          <button 
            className={`tab-btn ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            <i className="fas fa-newspaper"></i>
            News
          </button>
          <button 
            className={`tab-btn ${activeTab === 'facts' ? 'active' : ''}`}
            onClick={() => setActiveTab('facts')}
          >
            <i className="fas fa-lightbulb"></i>
            Facts
          </button>
        </div>

        <div className="tab-content-container">
          {loading && (
            <div className="loading-indicator">
              <i className="fas fa-spinner fa-spin"></i>
              Loading local information...
            </div>
          )}

          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {!loading && renderTabContent()}
        </div>
      </div>

      {/* I Love Southington Story - Hidden by default */}
      {showMore && (
        <div className="extended-intro">
          <div className="southington-story">
            <h3><i className="fas fa-heart"></i> The "I Love Southington" Story</h3>
            <p>I LOVE SOUTHINGTON is a song that was written & performed by Kevin Curtiss. I want to give you the lyrics, and maybe some day I will. For now just know that it's sung to the same rhythm and beat as I Love Rock-n-Roll by Joan Jett.</p>
            <p>It inspired me to come up with the I Love Southington logo that features an <i className="fas fa-apple-whole"></i> in the shape of a <i className="fas fa-heart"></i> to act as the "o" in "Love".</p>
          </div>
        </div>
      )}

      <div className="cta-buttons">
        <button className="cta-btn primary" onClick={handleCelebration}>
          <i className="fas fa-heart"></i>
          {showMore ? 'Hide Story' : 'I Love Southington'}
        </button>

        <div className="map-dropdown">
          <button className="cta-btn tertiary map-toggle" onClick={() => setShowMapOptions(!showMapOptions)}>
            <i className="fas fa-map"></i>
            View on Map
            <i className={`fas fa-chevron-${showMapOptions ? 'up' : 'down'}`}></i>
          </button>
          {showMapOptions && (
            <div className="map-options">
              <a 
                href="https://www.google.com/maps/place/Southington,+CT" 
                target="_blank" 
                rel="noreferrer"
                className="map-option"
              >
                <i className="fas fa-map-marker-alt"></i>
                Southington (All Areas)
              </a>
              <a 
                href="https://www.google.com/maps/place/Southington,+CT+06489" 
                target="_blank" 
                rel="noreferrer"
                className="map-option"
              >
                <i className="fas fa-map-marker-alt"></i>
                Southington (06489)
              </a>
              <a 
                href="https://www.google.com/maps/place/Plantsville,+CT+06479" 
                target="_blank" 
                rel="noreferrer"
                className="map-option"
              >
                <i className="fas fa-map-marker-alt"></i>
                Plantsville (06479)
              </a>
              <a 
                href="https://www.google.com/maps/place/Marion,+CT+06444" 
                target="_blank" 
                rel="noreferrer"
                className="map-option"
              >
                <i className="fas fa-map-marker-alt"></i>
                Marion (06444)
              </a>
              <a 
                href="https://www.google.com/maps/place/Milldale,+CT+06467" 
                target="_blank" 
                rel="noreferrer"
                className="map-option"
              >
                <i className="fas fa-map-marker-alt"></i>
                Milldale (06467)
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SouthingtonEnhanced;